import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
    Plus, Pencil, Trash2, X, AlertCircle, Package, Percent, Building2,
    ChevronRight, Landmark, Image,
} from 'lucide-react';
import { selectUser } from '../store/authSlice';
import { configApi } from '../api/configApi';

/**
 * Configuration: loan products, business entities (which own payees), the
 * org's base commission rates, and the logo drawn on its RCTIs. Four tabs.
 *
 * Business entities are the parent grouping: a company/office the org deals
 * with. Each entity owns payees (subcontractors and referrers). The entities
 * tab is a master/detail — pick an entity on the left, manage its payees on
 * the right. Every payee belongs to exactly one entity.
 *
 * Rates are stored as decimal fractions (1% = 0.01) and shown as percent —
 * the form converts on the boundary so the user always types/reads percent.
 */
export default function ConfigurationPage() {
    const user = useSelector(selectUser);
    const orgUuid = user?.organisationUuid;
    const [tab, setTab] = useState('products');

    return (
        <div className="px-8 py-8 max-w-5xl">
            <div className="mb-6">
                <h2 className="font-display text-2xl font-semibold text-slate-900">
                    Configuration
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                    Funders and their loan products, the business entities and payees you pay, and your base commission rates.
                </p>
            </div>

            <div className="flex gap-1 mb-6 border-b border-slate-200">
                <TabButton active={tab === 'products'} onClick={() => setTab('products')}
                           icon={<Package size={15} />} label="Products" />
                <TabButton active={tab === 'entities'} onClick={() => setTab('entities')}
                           icon={<Building2 size={15} />} label="Business entities" />
                <TabButton active={tab === 'rates'} onClick={() => setTab('rates')}
                           icon={<Percent size={15} />} label="Rates" />
                <TabButton active={tab === 'branding'} onClick={() => setTab('branding')}
                           icon={<Image size={15} />} label="Branding" />
            </div>

            {tab === 'products' && <ProductsTab orgUuid={orgUuid} />}
            {tab === 'entities' && <EntitiesTab orgUuid={orgUuid} />}
            {tab === 'rates' && <RatesTab orgUuid={orgUuid} />}
            {tab === 'branding' && <BrandingTab orgUuid={orgUuid} />}
        </div>
    );
}

function TabButton({ active, onClick, icon, label }) {
    return (
        <button
            onClick={onClick}
            className={[
                'flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors',
                active
                    ? 'border-brand-600 text-brand-700 font-medium'
                    : 'border-transparent text-slate-500 hover:text-slate-800',
            ].join(' ')}
        >
            {icon}
            {label}
        </button>
    );
}

/* ──────────────────────  Products grouped by funder (master/detail)  ────────────────────── */

function ProductsTab({ orgUuid }) {
    const [funders, setFunders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedUuid, setSelectedUuid] = useState(null);
    const [editingFunder, setEditingFunder] = useState(null); // funder | {} | null

    const reload = useCallback(() => {
        if (!orgUuid) return;
        setLoading(true);
        configApi.listFunders(orgUuid)
            .then((list) => {
                setFunders(list);
                setSelectedUuid((cur) => {
                    if (cur && list.some((f) => f.uuid === cur)) return cur;
                    return list[0]?.uuid ?? null;
                });
            })
            .catch((e) => setError(apiError(e)))
            .finally(() => setLoading(false));
    }, [orgUuid]);

    useEffect(() => { reload(); }, [reload]);

    const onDeleteFunder = (f) => {
        if (!window.confirm(`Delete funder "${f.name}"? This is only possible if it has no products.`)) return;
        configApi.deleteFunder(orgUuid, f.uuid)
            .then(reload)
            .catch((e) => setError(apiError(e)));
    };

    const selected = funders.find((f) => f.uuid === selectedUuid) ?? null;

    return (
        <>
            <Toolbar title="Funders" onAdd={() => setEditingFunder({})} addLabel="Add funder" />
            <ErrorBanner error={error} />

            <div className="grid grid-cols-[18rem,1fr] gap-4">
                {/* Master: funder list */}
                <Card>
                    <ul className="divide-y divide-slate-100">
                        {loading && <li className="px-4 py-8 text-center text-sm text-slate-400">Loading…</li>}
                        {!loading && funders.length === 0 && (
                            <li className="px-4 py-8 text-center text-sm text-slate-400">No funders yet.</li>
                        )}
                        {!loading && funders.map((f) => (
                            <li key={f.uuid}>
                                <button
                                    onClick={() => setSelectedUuid(f.uuid)}
                                    className={[
                                        'w-full flex items-center justify-between gap-2 px-4 py-3 text-left transition-colors',
                                        f.uuid === selectedUuid
                                            ? 'bg-brand-50 text-brand-800'
                                            : 'hover:bg-slate-50 text-slate-700',
                                    ].join(' ')}
                                >
                                    <span className="flex items-center gap-2 min-w-0">
                                        <Landmark size={15} className="flex-shrink-0 text-slate-400" />
                                        <span className="text-sm font-medium truncate">{f.name}</span>
                                    </span>
                                    <ChevronRight size={15} className="flex-shrink-0 text-slate-300" />
                                </button>
                            </li>
                        ))}
                    </ul>
                </Card>

                {/* Detail: selected funder + its products */}
                {!loading && (
                    selected ? (
                        <FunderDetail
                            orgUuid={orgUuid}
                            funder={selected}
                            onEdit={() => setEditingFunder(selected)}
                            onDelete={() => onDeleteFunder(selected)}
                            onError={setError}
                        />
                    ) : (
                        <Card>
                            <div className="px-6 py-16 text-center text-sm text-slate-400">
                                {funders.length === 0
                                    ? 'Add a funder to get started.'
                                    : 'Select a funder on the left.'}
                            </div>
                        </Card>
                    )
                )}
            </div>

            {editingFunder && (
                <FunderModal
                    orgUuid={orgUuid}
                    funder={editingFunder.uuid ? editingFunder : null}
                    onClose={() => setEditingFunder(null)}
                    onSaved={(saved) => {
                        setEditingFunder(null);
                        if (saved?.uuid) setSelectedUuid(saved.uuid);
                        reload();
                    }}
                    onError={setError}
                />
            )}
        </>
    );
}

/** Right-hand panel: the funder header + the products registered under it. */
function FunderDetail({ orgUuid, funder, onEdit, onDelete, onError }) {
    const [products, setProducts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editingProduct, setEditingProduct] = useState(null); // product | {} | null

    const reloadProducts = useCallback(() => {
        if (!orgUuid || !funder?.uuid) return;
        setLoading(true);
        configApi.listProductsByFunder(orgUuid, funder.uuid)
            .then(setProducts)
            .catch((e) => onError(apiError(e)))
            .finally(() => setLoading(false));
    }, [orgUuid, funder?.uuid, onError]);

    useEffect(() => { reloadProducts(); }, [reloadProducts]);

    const onDeleteProduct = (p) => {
        if (!window.confirm(`Delete product "${p.name}"?`)) return;
        configApi.deleteProduct(orgUuid, p.uuid).then(reloadProducts).catch((e) => onError(apiError(e)));
    };

    const list = products ?? [];

    return (
        <Card>
            <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="font-display text-lg font-semibold text-slate-900 truncate">{funder.name}</h3>
                    <p className="text-xs text-slate-500 mt-1">{list.length} product{list.length === 1 ? '' : 's'}</p>
                </div>
                <RowActions onEdit={onEdit} onDelete={onDelete} />
            </div>

            <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider">Products</h4>
                    <button
                        onClick={() => setEditingProduct({})}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 rounded-md"
                    >
                        <Plus size={13} /> Add product
                    </button>
                </div>
                <div className="border border-slate-200 rounded-md overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <Th>Name</Th><Th>Code</Th>
                            <Th align="right">Upfront</Th><Th align="right">Trail p.a.</Th>
                            <Th align="right" />
                        </tr>
                        </thead>
                        <tbody>
                        {loading && <LoadingRow cols={5} />}
                        {!loading && list.length === 0 && <EmptyRow cols={5} label="No products under this funder yet." />}
                        {!loading && list.map((p) => (
                            <tr key={p.uuid} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                                <Td className="font-medium text-slate-900">{p.name}</Td>
                                <Td>{p.code ? <Code>{p.code}</Code> : <Muted>—</Muted>}</Td>
                                <Td align="right">{pct(p.funderUpfrontRate)}</Td>
                                <Td align="right">{pct(p.funderTrailRate)}</Td>
                                <Td align="right"><RowActions onEdit={() => setEditingProduct(p)} onDelete={() => onDeleteProduct(p)} /></Td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {editingProduct && (
                <ProductModal
                    orgUuid={orgUuid}
                    funder={funder}
                    product={editingProduct.uuid ? editingProduct : null}
                    onClose={() => setEditingProduct(null)}
                    onSaved={() => { setEditingProduct(null); reloadProducts(); }}
                    onError={onError}
                />
            )}
        </Card>
    );
}

function FunderModal({ orgUuid, funder, onClose, onSaved, onError }) {
    const [name, setName] = useState(funder?.name ?? '');
    const [saving, setSaving] = useState(false);

    const error = vRequired(name, 'Name', MAX.name);
    const canSave = !error;

    const submit = async () => {
        if (!canSave) return;
        setSaving(true);
        try {
            const saved = funder
                ? await configApi.updateFunder(orgUuid, funder.uuid, { name: name.trim() })
                : await configApi.createFunder(orgUuid, { name: name.trim() });
            onSaved(saved);
        } catch (e) {
            onError(apiError(e));
            setSaving(false);
        }
    };

    return (
        <Modal title={funder ? 'Edit funder' : 'Add funder'} onClose={onClose}>
            <Field label="Name" error={error}>
                <Input value={name} onChange={setName} invalid={!!error} autoFocus />
            </Field>
            <ModalActions onCancel={onClose} onSave={submit} saving={saving} disabled={!canSave} />
        </Modal>
    );
}

function ProductModal({ orgUuid, funder, product, onClose, onSaved, onError }) {
    const [form, setForm] = useState(() => ({
        name: product?.name ?? '',
        code: product?.code ?? '',
        funderUpfrontPct: product ? toPct(product.funderUpfrontRate) : '',
        funderTrailPct: product ? toPct(product.funderTrailRate) : '',
    }));
    const [saving, setSaving] = useState(false);

    const errors = {
        name: vRequired(form.name, 'Name', MAX.name),
        code: vOptionalLen(form.code, 'Code', MAX.code),
        funderUpfrontPct: vRate(form.funderUpfrontPct, 'Upfront rate'),
        funderTrailPct: vRate(form.funderTrailPct, 'Trail rate'),
    };
    const canSave = noErrors(errors);

    const submit = () => {
        if (!canSave) return;
        setSaving(true);
        const payload = {
            funderUuid: funder.uuid,        // product belongs to this funder
            name: form.name.trim(),
            code: form.code.trim() || null, // optional; blank → null
            funderUpfrontRate: fromPct(form.funderUpfrontPct),
            funderTrailRate: fromPct(form.funderTrailPct),
        };
        const req = product
            ? configApi.updateProduct(orgUuid, product.uuid, payload)
            : configApi.createProduct(orgUuid, payload);
        req.then(onSaved).catch((e) => { onError(apiError(e)); setSaving(false); });
    };

    return (
        <Modal title={product ? 'Edit product' : `Add product to ${funder.name}`} onClose={onClose}>
            <Field label="Name" error={errors.name}>
                <Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} invalid={!!errors.name} autoFocus />
            </Field>
            <Field label="Code" error={errors.code} hint="Optional — short code, unique per organisation when provided">
                <Input value={form.code} onChange={(v) => setForm({ ...form, code: v })} invalid={!!errors.code} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Upfront rate" error={errors.funderUpfrontPct} hint="% received from funder">
                    <PctInput value={form.funderUpfrontPct} onChange={(v) => setForm({ ...form, funderUpfrontPct: v })} />
                </Field>
                <Field label="Trail rate p.a." error={errors.funderTrailPct} hint="% received from funder">
                    <PctInput value={form.funderTrailPct} onChange={(v) => setForm({ ...form, funderTrailPct: v })} />
                </Field>
            </div>
            <ModalActions onCancel={onClose} onSave={submit} saving={saving} disabled={!canSave} />
        </Modal>
    );
}


/* ──────────────────────  Business entities (master/detail)  ────────────────────── */

function EntitiesTab({ orgUuid }) {
    const [entities, setEntities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedUuid, setSelectedUuid] = useState(null);
    const [editingEntity, setEditingEntity] = useState(null); // entity | {} | null
    const [side, setSide] = useState('DISTRIBUTION_PARTNER'); // active sub-tab

    const reload = useCallback(() => {
        if (!orgUuid) return;
        setLoading(true);
        configApi.listBusinessEntities(orgUuid)
            .then((list) => {
                setEntities(list);
                // keep selection if still present, else select first
                setSelectedUuid((cur) => {
                    if (cur && list.some((e) => e.uuid === cur)) return cur;
                    return list[0]?.uuid ?? null;
                });
            })
            .catch((e) => setError(apiError(e)))
            .finally(() => setLoading(false));
    }, [orgUuid]);

    useEffect(() => { reload(); }, [reload]);

    const onDeleteEntity = (ent) => {
        if (!window.confirm(`Delete business entity "${ent.name}" and unlink its payees? This cannot be undone.`)) return;
        configApi.deleteBusinessEntity(orgUuid, ent.uuid)
            .then(reload)
            .catch((e) => setError(apiError(e)));
    };

    // Entities shown on the active tab: exactly one side, no mixing.
    const visible = entities.filter((e) => e.entityType === side);

    // When the tab changes, keep the selection only if it's on this tab;
    // otherwise select the first entity of the tab (or clear).
    useEffect(() => {
        setSelectedUuid((cur) => {
            if (cur && visible.some((e) => e.uuid === cur)) return cur;
            return visible[0]?.uuid ?? null;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [side, entities]);

    const selected = visible.find((e) => e.uuid === selectedUuid) ?? null;

    const SIDES = [
        ['DISTRIBUTION_PARTNER', 'Distribution Partner entities'],
        ['REFERRER', 'Referrer entities'],
    ];

    return (
        <>
            <Toolbar
                title="Business entities"
                onAdd={() => setEditingEntity({ entityType: side })}
                addLabel={side === 'REFERRER' ? 'Add Referrer entity' : 'Add Distribution Partner entity'}
            />
            <ErrorBanner error={error} />

            {/* Side sub-tabs: Distribution Partner | Referrer, never mixed. */}
            <div className="flex gap-1 mb-4 border-b border-slate-200">
                {SIDES.map(([value, label]) => {
                    const count = entities.filter((e) => e.entityType === value).length;
                    const active = side === value;
                    return (
                        <button
                            key={value}
                            onClick={() => setSide(value)}
                            className={[
                                'px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors',
                                active
                                    ? 'border-brand-600 text-brand-700'
                                    : 'border-transparent text-slate-500 hover:text-slate-800',
                            ].join(' ')}
                        >
                            {label}
                            <span className="ml-1.5 text-xs text-slate-400 tabular-nums">{count}</span>
                        </button>
                    );
                })}
            </div>

            <div className="grid grid-cols-[18rem,1fr] gap-4">
                {/* Master: entity list (active side only) */}
                <Card>
                    <ul className="divide-y divide-slate-100">
                        {loading && <li className="px-4 py-8 text-center text-sm text-slate-400">Loading…</li>}
                        {!loading && visible.length === 0 && (
                            <li className="px-4 py-8 text-center text-sm text-slate-400">
                                No {side === 'REFERRER' ? 'Referrer' : 'Distribution Partner'} entities yet.
                            </li>
                        )}
                        {!loading && visible.map((ent) => (
                            <li key={ent.uuid}>
                                <button
                                    onClick={() => setSelectedUuid(ent.uuid)}
                                    className={[
                                        'w-full flex items-center justify-between gap-2 px-4 py-3 text-left transition-colors',
                                        ent.uuid === selectedUuid
                                            ? 'bg-brand-50 text-brand-800'
                                            : 'hover:bg-slate-50 text-slate-700',
                                    ].join(' ')}
                                >
                                    <span className="min-w-0">
                                        <span className="block text-sm font-medium truncate">{ent.name}</span>
                                        <span className="block text-xs text-slate-400 truncate">
                                            {ent.abn ? `ABN ${ent.abn}` : '—'}
                                        </span>
                                    </span>
                                    <ChevronRight size={15} className="flex-shrink-0 text-slate-300" />
                                </button>
                            </li>
                        ))}
                    </ul>
                </Card>

                {/* Detail: selected entity + its payees */}
                {!loading && (
                    selected ? (
                        <EntityDetail
                            orgUuid={orgUuid}
                            entity={selected}
                            onEdit={() => setEditingEntity(selected)}
                            onDelete={() => onDeleteEntity(selected)}
                            onError={setError}
                            onEntityChanged={reload}
                        />
                    ) : (
                        <Card>
                            <div className="px-6 py-16 text-center text-sm text-slate-400">
                                {visible.length === 0
                                    ? `Add a ${side === 'REFERRER' ? 'Referrer' : 'Distribution Partner'} entity to get started.`
                                    : 'Select a business entity on the left.'}
                            </div>
                        </Card>
                    )
                )}
            </div>

            {editingEntity && (
                <EntityModal
                    orgUuid={orgUuid}
                    entity={editingEntity.uuid ? editingEntity : null}
                    defaultType={editingEntity.entityType ?? side}
                    onClose={() => setEditingEntity(null)}
                    onSaved={(saved) => {
                        setEditingEntity(null);
                        if (saved?.entityType) setSide(saved.entityType);
                        if (saved?.uuid) setSelectedUuid(saved.uuid);
                        reload();
                    }}
                    onError={setError}
                />
            )}
        </>
    );
}

/**
 * Right-hand detail panel: the entity's identity + contact, and its payees
 * split into subcontractors and referrers. Loads payees for the entity;
 * prefers entity.payees if the list endpoint already inlined them.
 */
function EntityDetail({ orgUuid, entity, onEdit, onDelete, onError, onEntityChanged }) {
    const [payees, setPayees] = useState(entity.payees ?? null);
    const [loading, setLoading] = useState(!entity.payees);
    const [editingPayee, setEditingPayee] = useState(null); // payee | {type} | null

    const reloadPayees = useCallback(() => {
        if (!orgUuid || !entity?.uuid) return;
        setLoading(true);
        configApi.listPayeesByEntity(orgUuid, entity.uuid)
            .then(setPayees)
            .catch((e) => onError(apiError(e)))
            .finally(() => setLoading(false));
    }, [orgUuid, entity?.uuid, onError]);

    // When the selected entity changes: use inlined payees if present, else fetch.
    useEffect(() => {
        if (entity.payees) {
            setPayees(entity.payees);
            setLoading(false);
        } else {
            reloadPayees();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entity.uuid]);

    const onDeletePayee = (p) => {
        if (!window.confirm(`Delete payee "${p.name}"?`)) return;
        configApi.deletePayee(orgUuid, p.uuid).then(reloadPayees).catch((e) => onError(apiError(e)));
    };

    const list = payees ?? [];
    // An entity belongs to exactly one side, so its payees are all that side's
    // type. Show a single group for the entity's side (no mixed list).
    const isReferrer = entity.entityType === 'REFERRER';
    const payeeType = isReferrer ? 'REFERRER' : 'SUBCONTRACTOR';
    const sidePayees = list.filter((p) => p.type === payeeType);
    const groupTitle = isReferrer ? 'Referrers' : 'Distribution Partners';

    return (
        <Card>
            {/* entity header */}
            <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <h3 className="font-display text-lg font-semibold text-slate-900 truncate">{entity.name}</h3>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                        {entity.abn ? `ABN ${entity.abn}` : <Muted>No ABN</Muted>}
                        {entity.contactName ? ` · ${entity.contactName}` : ''}
                        {entity.contactEmail ? ` · ${entity.contactEmail}` : ''}
                    </p>
                </div>
                <RowActions onEdit={onEdit} onDelete={onDelete} />
            </div>

            {/* payees for this entity's side only */}
            <div className="p-5 space-y-6">
                <PayeeGroup
                    title={groupTitle}
                    payees={sidePayees}
                    loading={loading}
                    onAdd={() => setEditingPayee({ type: payeeType })}
                    onEdit={(p) => setEditingPayee(p)}
                    onDelete={onDeletePayee}
                />
            </div>

            {editingPayee && (
                <PayeeModal
                    orgUuid={orgUuid}
                    entity={entity}
                    payee={editingPayee.uuid ? editingPayee : null}
                    defaultType={editingPayee.type ?? payeeType}
                    onClose={() => setEditingPayee(null)}
                    onSaved={() => { setEditingPayee(null); reloadPayees(); onEntityChanged?.(); }}
                    onError={onError}
                />
            )}
        </Card>
    );
}

/** A titled block listing payees of one type, with its own add button. */
function PayeeGroup({ title, payees, loading, onAdd, onEdit, onDelete }) {
    return (
        <section>
            <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider">{title}</h4>
                <button
                    onClick={onAdd}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 rounded-md"
                >
                    <Plus size={13} /> Add
                </button>
            </div>
            <div className="border border-slate-200 rounded-md overflow-hidden">
                {loading ? (
                    <div className="px-4 py-6 text-center text-sm text-slate-400">Loading…</div>
                ) : payees.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-slate-400">None yet.</div>
                ) : (
                    <ul className="divide-y divide-slate-100">
                        {payees.map((p) => (
                            <li key={p.uuid} className="px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50">
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-slate-900 truncate">{p.name}</p>
                                    <p className="text-xs text-slate-500 truncate">
                                        {p.contactName || <Muted>—</Muted>}
                                        {p.contactEmail ? ` · ${p.contactEmail}` : ''}
                                    </p>
                                </div>
                                <Code>{p.code}</Code>
                                <RowActions onEdit={() => onEdit(p)} onDelete={() => onDelete(p)} />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}

function EntityModal({ orgUuid, entity, defaultType, onClose, onSaved, onError }) {
    const [form, setForm] = useState(() => ({
        name: entity?.name ?? '',
        entityType: entity?.entityType ?? defaultType ?? 'DISTRIBUTION_PARTNER',
        abn: entity?.abn ?? '',
        contactName: entity?.contactName ?? '',
        contactEmail: entity?.contactEmail ?? '',
        contactPhone: entity?.contactPhone ?? '',
        address: entity?.address ?? '',
        brn: entity?.brn ?? '',
        gstRegistered: entity?.gstRegistered ?? false,
    }));
    const [saving, setSaving] = useState(false);

    const isReferrer = form.entityType === 'REFERRER';
    const errors = {
        name: vRequired(form.name, 'Name', MAX.name),
        // ABN, Email and Mobile are mandatory for both entity sides: require a
        // value first, then apply the format check.
        abn: vRequired(form.abn, 'ABN', MAX.abn) || vAbn(form.abn),
        address: vOptionalLen(form.address, 'Address', MAX.address),
        contactName: vOptionalLen(form.contactName, 'Contact name', MAX.contactName),
        contactEmail: (form.contactEmail ?? '').trim()
            ? vEmail(form.contactEmail) : 'Email is required.',
        contactPhone: (form.contactPhone ?? '').trim()
            ? vPhone(form.contactPhone) : 'Mobile is required.',
        // Referrer-only mandatory business reference. IRN now lives at the
        // individual (payee) level, not on the entity.
        brn: isReferrer ? vRequired(form.brn, 'BRN', 64) : '',
    };
    const canSave = noErrors(errors);

    const submit = async () => {
        if (!canSave) return;
        setSaving(true);
        const payload = {
            name: form.name.trim(),
            entityType: form.entityType,
            abn: form.abn.trim() || null,
            contactName: form.contactName.trim() || null,
            contactEmail: form.contactEmail.trim() || null,
            contactPhone: form.contactPhone.trim() || null,
            address: form.address.trim() || null,
            brn: isReferrer ? (form.brn.trim() || null) : null,
            gstRegistered: form.gstRegistered,
        };
        try {
            const saved = entity
                ? await configApi.updateBusinessEntity(orgUuid, entity.uuid, payload)
                : await configApi.createBusinessEntity(orgUuid, payload);
            onSaved(saved);
        } catch (e) {
            onError(apiError(e));
            setSaving(false);
        }
    };

    const typeLabel = form.entityType === 'REFERRER'
        ? 'Referrer entity' : 'Distribution Partner entity';

    return (
        <Modal title={`${entity ? 'Edit' : 'Add'} ${typeLabel}`} onClose={onClose}>
            <Field label="Name" error={errors.name}>
                <Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} invalid={!!errors.name} autoFocus />
            </Field>
            <Field label="Type" hint={entity ? "An entity's type can't be changed after it's created" : 'Set by the tab you added from'}>
                <div className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-slate-50 text-slate-600">
                    {typeLabel}
                </div>
            </Field>
            <Field label="ABN" error={errors.abn} hint="11 digits · required">
                <Input value={form.abn} maxLength={11}
                       onChange={(v) => setForm({ ...form, abn: v.replace(/\D/g, '').slice(0, 11) })}
                       invalid={!!errors.abn} />
            </Field>
            {isReferrer && (
                <Field label="BRN" error={errors.brn} hint="Referrer business reference · required">
                    <Input value={form.brn} onChange={(v) => setForm({ ...form, brn: v })} invalid={!!errors.brn} />
                </Field>
            )}
            <Field label="Address" error={errors.address}>
                <Input value={form.address} onChange={(v) => setForm({ ...form, address: v })} invalid={!!errors.address} />
            </Field>
            <Field label="Contact name" error={errors.contactName}>
                <Input value={form.contactName} onChange={(v) => setForm({ ...form, contactName: v })} invalid={!!errors.contactName} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Email" error={errors.contactEmail} hint="Required">
                    <Input type="email" value={form.contactEmail} onChange={(v) => setForm({ ...form, contactEmail: v })} invalid={!!errors.contactEmail} />
                </Field>
                <Field label="Mobile" error={errors.contactPhone} hint="Required">
                    <Input value={form.contactPhone} onChange={(v) => setForm({ ...form, contactPhone: v })} invalid={!!errors.contactPhone} />
                </Field>
            </div>
            <Field label="GST">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={form.gstRegistered}
                        onChange={(e) => setForm({ ...form, gstRegistered: e.target.checked })}
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                    Registered for GST
                </label>
            </Field>
            <ModalActions onCancel={onClose} onSave={submit} saving={saving} disabled={!canSave} />
        </Modal>
    );
}

function PayeeModal({ orgUuid, entity, payee, defaultType, onClose, onSaved, onError }) {
    const [form, setForm] = useState(() => ({
        type: payee?.type ?? defaultType ?? 'SUBCONTRACTOR',
        name: payee?.name ?? '',
        code: payee?.code ?? '',
        irn: payee?.irn ?? '',
        contactName: payee?.contactName ?? '',
        contactEmail: payee?.contactEmail ?? '',
        contactPhone: payee?.contactPhone ?? '',
    }));
    const [saving, setSaving] = useState(false);

    const isReferrer = form.type === 'REFERRER';
    const errors = {
        name: vRequired(form.name, 'Name', MAX.name),
        code: vOptionalLen(form.code, 'Code', MAX.code),
        // IRN (individual reference) is mandatory for referrer-type payees only.
        irn: isReferrer ? vRequired(form.irn, 'IRN', 64) : '',
        contactName: vOptionalLen(form.contactName, 'Contact name', MAX.contactName),
        contactEmail: (form.contactEmail ?? '').trim()
            ? vEmail(form.contactEmail) : 'Contact email is required.',
        contactPhone: (form.contactPhone ?? '').trim()
            ? vPhone(form.contactPhone) : 'Contact phone is required.',
    };
    const canSave = noErrors(errors);

    const submit = async () => {
        if (!canSave) return;
        setSaving(true);
        const payload = {
            businessEntityUuid: entity.uuid,   // every payee belongs to this entity
            type: form.type,
            name: form.name.trim(),
            code: form.code.trim() || null,    // optional; blank → null
            irn: isReferrer ? (form.irn.trim() || null) : null,
            contactName: form.contactName.trim() || null,
            contactEmail: form.contactEmail.trim() || null,
            contactPhone: form.contactPhone.trim() || null,
        };
        try {
            if (payee) {
                await configApi.updatePayee(orgUuid, payee.uuid, payload);
            } else {
                await configApi.createPayee(orgUuid, payload);
            }
            onSaved();
        } catch (e) {
            onError(apiError(e));
            setSaving(false);
        }
    };

    return (
        <Modal title={payee ? 'Edit payee' : `Add payee to ${entity.name}`} onClose={onClose}>
            <Field label="Type" hint="Set by the business entity's type">
                <div className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-slate-50 text-slate-600">
                    {form.type === 'REFERRER' ? 'Referrer' : 'Distribution Partner'}
                </div>
            </Field>
            <Field label="Name" error={errors.name}>
                <Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} invalid={!!errors.name} autoFocus />
            </Field>
            {isReferrer && (
                <Field label="IRN" error={errors.irn} hint="Individual reference · required">
                    <Input value={form.irn} onChange={(v) => setForm({ ...form, irn: v })} invalid={!!errors.irn} />
                </Field>
            )}
            {/* Code field hidden — form.code, its validator, and the save payload
                below still carry the value through unchanged so an existing payee's
                code is preserved on edit. Restore this block to re-expose the UI. */}
            {/*
            <Field label="Code" error={errors.code} hint="Optional — used to match CRM loan rows; unique when provided">
                <Input value={form.code} onChange={(v) => setForm({ ...form, code: v })} invalid={!!errors.code} />
            </Field>
            */}
            <Field label="Contact name" error={errors.contactName}>
                <Input value={form.contactName} onChange={(v) => setForm({ ...form, contactName: v })} invalid={!!errors.contactName} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Contact email" error={errors.contactEmail}>
                    <Input type="email" value={form.contactEmail} onChange={(v) => setForm({ ...form, contactEmail: v })} invalid={!!errors.contactEmail} />
                </Field>
                <Field label="Contact phone" error={errors.contactPhone}>
                    <Input value={form.contactPhone} onChange={(v) => setForm({ ...form, contactPhone: v })} invalid={!!errors.contactPhone} />
                </Field>
            </div>
            <ModalActions onCancel={onClose} onSave={submit} saving={saving} disabled={!canSave} />
        </Modal>
    );
}

/* ──────────────────────────────  Rates  ─────────────────────────────── */

/**
 * The org's single base rate config — six rates (funder / subcontractor /
 * referrer, each upfront + trail), plus four optional CRM column names that
 * carry per-loan rate overrides. One form, loaded once and saved as a whole.
 * GET returns null fields when nothing is saved yet, so the form starts blank.
 */
function RatesTab({ orgUuid }) {
    const [form, setForm] = useState(null);   // null until loaded
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [savedAt, setSavedAt] = useState(null);

    useEffect(() => {
        if (!orgUuid) return;
        let cancelled = false;
        setLoading(true);
        configApi.getRateConfig(orgUuid)
            .then((cfg) => {
                if (cancelled) return;
                setForm({
                    funderUpfrontPct:   cfg?.funderUpfrontRate   != null ? toPct(cfg.funderUpfrontRate)   : '',
                    funderTrailPct:     cfg?.funderTrailRate     != null ? toPct(cfg.funderTrailRate)     : '',
                    subbyUpfrontPct:    cfg?.subbyUpfrontRate    != null ? toPct(cfg.subbyUpfrontRate)    : '',
                    subbyTrailPct:      cfg?.subbyTrailRate      != null ? toPct(cfg.subbyTrailRate)      : '',
                    referrerUpfrontPct: cfg?.referrerUpfrontRate != null ? toPct(cfg.referrerUpfrontRate) : '',
                    referrerTrailPct:   cfg?.referrerTrailRate   != null ? toPct(cfg.referrerTrailRate)   : '',
                    subbyUpfrontRateColumn:    cfg?.subbyUpfrontRateColumn    ?? '',
                    subbyTrailRateColumn:      cfg?.subbyTrailRateColumn      ?? '',
                    referrerUpfrontRateColumn: cfg?.referrerUpfrontRateColumn ?? '',
                    referrerTrailRateColumn:   cfg?.referrerTrailRateColumn   ?? '',
                });
            })
            .catch((e) => !cancelled && setError(apiError(e)))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [orgUuid]);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const save = () => {
        setSaving(true);
        setError(null);
        setSavedAt(null);
        const payload = {
            funderUpfrontRate:   fromPct(form.funderUpfrontPct || '0'),
            funderTrailRate:     fromPct(form.funderTrailPct || '0'),
            subbyUpfrontRate:    fromPct(form.subbyUpfrontPct || '0'),
            subbyTrailRate:      fromPct(form.subbyTrailPct || '0'),
            referrerUpfrontRate: fromPct(form.referrerUpfrontPct || '0'),
            referrerTrailRate:   fromPct(form.referrerTrailPct || '0'),
            subbyUpfrontRateColumn:    form.subbyUpfrontRateColumn.trim()    || null,
            subbyTrailRateColumn:      form.subbyTrailRateColumn.trim()      || null,
            referrerUpfrontRateColumn: form.referrerUpfrontRateColumn.trim() || null,
            referrerTrailRateColumn:   form.referrerTrailRateColumn.trim()   || null,
        };
        configApi.saveRateConfig(orgUuid, payload)
            .then(() => setSavedAt(Date.now()))
            .catch((e) => setError(apiError(e)))
            .finally(() => setSaving(false));
    };

    return (
        <>
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">Base rates</h3>
            </div>
            <ErrorBanner error={error} />
            <Card>
                {loading || !form ? (
                    <p className="px-4 py-8 text-center text-sm text-slate-400">Loading…</p>
                ) : (
                    <div className="p-5 space-y-5">
                        <p className="text-sm text-slate-500">
                            The base commission rates applied to a run. The run screen pre-fills from
                            these and you can still adjust them per run. Trail rates are per annum.
                        </p>
                        <RateRow label="Funder" hint="What you receive from the funder"
                                 upfront={form.funderUpfrontPct} trail={form.funderTrailPct}
                                 onUpfront={(v) => set({ funderUpfrontPct: v })} onTrail={(v) => set({ funderTrailPct: v })} />
                        <RateRow label="Subcontractor" hint="What the subcontractor (office) receives"
                                 upfront={form.subbyUpfrontPct} trail={form.subbyTrailPct}
                                 onUpfront={(v) => set({ subbyUpfrontPct: v })} onTrail={(v) => set({ subbyTrailPct: v })} />
                        <RateRow label="Referrer" hint="Paid out of the subcontractor's slice"
                                 upfront={form.referrerUpfrontPct} trail={form.referrerTrailPct}
                                 onUpfront={(v) => set({ referrerUpfrontPct: v })} onTrail={(v) => set({ referrerTrailPct: v })} />

                        {/* Special-rate columns section hidden — form state + save payload
                            below still carry these values through unchanged so saved
                            config is preserved. Restore this block to re-expose the UI. */}
                        {/*
                        <div className="pt-3 border-t border-slate-100 space-y-3">
                            <div>
                                <p className="text-sm font-medium text-slate-800">
                                    Special-rate columns{' '}
                                    <span className="font-normal text-slate-400">(optional)</span>
                                </p>
                                <p className="text-xs text-slate-400 mt-0.5">
                                    If the CRM file carries a per-loan rate in a column, name it
                                    here. When a loan has a non-zero value in that column, the run
                                    uses it for that loan instead of the rate above. Leave blank to
                                    always use the rates above.
                                </p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Subcontractor upfront column">
                                    <Input
                                        value={form.subbyUpfrontRateColumn}
                                        onChange={(v) => set({ subbyUpfrontRateColumn: v })}
                                    />
                                </Field>
                                <Field label="Subcontractor trail column">
                                    <Input
                                        value={form.subbyTrailRateColumn}
                                        onChange={(v) => set({ subbyTrailRateColumn: v })}
                                    />
                                </Field>
                                <Field label="Referrer upfront column">
                                    <Input
                                        value={form.referrerUpfrontRateColumn}
                                        onChange={(v) => set({ referrerUpfrontRateColumn: v })}
                                    />
                                </Field>
                                <Field label="Referrer trail column">
                                    <Input
                                        value={form.referrerTrailRateColumn}
                                        onChange={(v) => set({ referrerTrailRateColumn: v })}
                                    />
                                </Field>
                            </div>
                        </div>
                        */}

                        <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
                            {savedAt && <span className="text-sm text-green-600">Saved</span>}
                            <button
                                onClick={save}
                                disabled={saving}
                                className="px-4 py-1.5 text-sm bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:opacity-50"
                            >
                                {saving ? 'Saving…' : 'Save rates'}
                            </button>
                        </div>
                    </div>
                )}
            </Card>
        </>
    );
}

/** One labelled pair of upfront / trail percent inputs. */
function RateRow({ label, hint, upfront, trail, onUpfront, onTrail }) {
    return (
        <div className="grid grid-cols-[1fr,auto,auto] items-start gap-4">
            <div>
                <p className="text-sm font-medium text-slate-800">{label}</p>
                {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
            </div>
            <Field label="Upfront">
                <PctInput value={upfront} onChange={onUpfront} small />
            </Field>
            <Field label="Trail p.a.">
                <PctInput value={trail} onChange={onTrail} small />
            </Field>
        </div>
    );
}

/* ────────────────────────────  Shared UI  ───────────────────────────── */

function Toolbar({ title, onAdd, addLabel }) {
    return (
        <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">{title}</h3>
            <button
                onClick={onAdd}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-md hover:bg-brand-700"
            >
                <Plus size={15} /> {addLabel}
            </button>
        </div>
    );
}

/**
 * Branding: the logo drawn at the top of every RCTI the org issues.
 *
 * The preview is fetched as a blob through the authenticated axios client, not
 * pointed at by an <img src> — a bare <img> cannot carry the Authorization
 * header, so it would break as soon as those routes stop being anonymous. The
 * object URL is revoked on replace and on unmount; without that every upload
 * leaks a blob for the life of the tab.
 */
function BrandingTab({ orgUuid }) {
    const [info, setInfo] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        if (!orgUuid) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        configApi.getLogoInfo(orgUuid)
            .then((i) => {
                if (cancelled) return;
                setInfo(i);
                if (!i?.present) {
                    setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return null; });
                    return undefined;
                }
                return configApi.fetchLogoObjectUrl(orgUuid).then((url) => {
                    if (cancelled) { if (url) URL.revokeObjectURL(url); return; }
                    setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
                });
            })
            .catch((e) => !cancelled && setError(apiError(e)))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [orgUuid]);

    useEffect(() => load(), [load]);

    // Revoke on unmount. The state updater above handles replacement; this
    // catches the tab being navigated away from with a preview still held.
    useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

    const onPick = (e) => {
        const file = e.target.files?.[0];
        // Reset the input's value so re-picking the SAME file fires onChange
        // again — otherwise a failed upload cannot be retried without first
        // choosing a different file.
        e.target.value = '';
        if (!file) return;
        setBusy(true);
        setError(null);
        configApi.uploadLogo(orgUuid, file)
            .then(() => load())
            .catch((err) => setError(apiError(err)))
            .finally(() => setBusy(false));
    };

    const onRemove = () => {
        setBusy(true);
        setError(null);
        configApi.deleteLogo(orgUuid)
            .then(() => load())
            .catch((err) => setError(apiError(err)))
            .finally(() => setBusy(false));
    };

    return (
        <Card>
            <div className="px-5 py-4 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-slate-800">RCTI logo</h3>
                <p className="text-xs text-slate-500 mt-1">
                    Drawn at the top of every Recipient Created Tax Invoice this
                    organisation issues. PNG or JPEG, up to 2 MB.
                </p>
            </div>

            <div className="p-5 space-y-4">
                {error && (
                    <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                        <AlertCircle size={15} className="mt-0.5 shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                {loading ? (
                    <p className="text-sm text-slate-400">Loading…</p>
                ) : (
                    <>
                        <div className="flex items-start gap-5">
                            <div className="w-56 h-28 border border-dashed border-slate-300 rounded-md flex items-center justify-center bg-slate-50 overflow-hidden shrink-0">
                                {previewUrl ? (
                                    <img
                                        src={previewUrl}
                                        alt="RCTI logo"
                                        className="max-w-full max-h-full object-contain"
                                    />
                                ) : (
                                    <span className="text-xs text-slate-400">No logo set</span>
                                )}
                            </div>

                            <div className="text-sm space-y-1 pt-1">
                                {info?.present ? (
                                    <>
                                        <p className="text-slate-800 font-medium">
                                            {info.filename || 'Logo'}
                                        </p>
                                        <p className="text-xs text-slate-500">{info.contentType}</p>
                                        {info.uploadedAt && (
                                            <p className="text-xs text-slate-400">
                                                Uploaded {new Date(info.uploadedAt).toLocaleString()}
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <p className="text-slate-500">
                                        Invoices render without a logo until one is uploaded.
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                            <label className="px-4 py-1.5 text-sm bg-brand-600 text-white rounded-md hover:bg-brand-700 cursor-pointer disabled:opacity-50">
                                {busy ? 'Working…' : info?.present ? 'Replace logo' : 'Upload logo'}
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg"
                                    className="hidden"
                                    disabled={busy}
                                    onChange={onPick}
                                />
                            </label>
                            {info?.present && (
                                <button
                                    onClick={onRemove}
                                    disabled={busy}
                                    className="px-4 py-1.5 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50"
                                >
                                    Remove
                                </button>
                            )}
                            <span className="text-xs text-slate-400">
                                Applies to the next run — invoices already generated are not re-rendered.
                            </span>
                        </div>
                    </>
                )}
            </div>
        </Card>
    );
}

function Card({ children }) {
    return <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">{children}</div>;
}

function RowActions({ onEdit, onDelete }) {
    return (
        <div className="flex items-center justify-end gap-1">
            <button onClick={onEdit} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md" aria-label="Edit">
                <Pencil size={15} />
            </button>
            <button onClick={onDelete} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md" aria-label="Delete">
                <Trash2 size={15} />
            </button>
        </div>
    );
}

function Modal({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 flex-shrink-0">
                    <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
                    <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700 rounded-md" aria-label="Close">
                        <X size={18} />
                    </button>
                </div>
                <div className="px-5 py-4 space-y-3.5 overflow-y-auto">{children}</div>
            </div>
        </div>
    );
}

function ModalActions({ onCancel, onSave, saving, disabled = false }) {
    return (
        <div className="flex justify-end gap-2 pt-2">
            <button onClick={onCancel} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-md">
                Cancel
            </button>
            <button onClick={onSave} disabled={saving || disabled}
                    className="px-4 py-1.5 text-sm bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
            </button>
        </div>
    );
}

function Field({ label, hint, error, children }) {
    return (
        <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
            {children}
            {error
                ? <p className="text-xs text-red-600 mt-1">{error}</p>
                : hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
        </div>
    );
}

function Input({ value, onChange, type = 'text', autoFocus, invalid = false, maxLength }) {
    return (
        <input
            type={type}
            value={value}
            autoFocus={autoFocus}
            maxLength={maxLength}
            onChange={(e) => onChange(e.target.value)}
            className={[
                'w-full text-sm border rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:border-transparent',
                invalid
                    ? 'border-red-400 focus:ring-red-400'
                    : 'border-slate-300 focus:ring-brand-500',
            ].join(' ')}
        />
    );
}

function PctInput({ value, onChange, small }) {
    return (
        <div className={`relative inline-block ${small ? 'w-24' : 'w-full'}`}>
            <input
                type="number"
                step="0.0001"
                min="0"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full text-sm border border-slate-300 rounded-md pl-3 pr-6 py-1.5 text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
        </div>
    );
}

function Th({ children, align = 'left' }) {
    return (
        <th className={`px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider ${align === 'right' ? 'text-right' : 'text-left'}`}>
            {children}
        </th>
    );
}

function Td({ children, align = 'left', className = '' }) {
    return (
        <td className={`px-4 py-3 text-sm text-slate-600 ${align === 'right' ? 'text-right' : ''} ${className}`}>
            {children}
        </td>
    );
}

function Code({ children }) {
    return <span className="font-mono text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{children}</span>;
}

function Muted({ children }) {
    return <span className="text-slate-300">{children}</span>;
}

function LoadingRow({ cols }) {
    return <tr><td colSpan={cols} className="px-4 py-8 text-center text-sm text-slate-400">Loading…</td></tr>;
}

function EmptyRow({ cols, label }) {
    return <tr><td colSpan={cols} className="px-4 py-8 text-center text-sm text-slate-400">{label}</td></tr>;
}

function ErrorBanner({ error }) {
    if (!error) return null;
    return (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm text-red-700">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
        </div>
    );
}

/* ─────────────────────────────  Helpers  ────────────────────────────── */

// Stored decimal fraction → percent string for display (0.007 → "0.7").
function toPct(fraction) {
    return String(Number((Number(fraction) * 100).toFixed(6)));
}
// Percent display string → decimal fraction string for the API ("0.7" → "0.007").
function fromPct(pctStr) {
    const n = Number(pctStr);
    return Number.isFinite(n) ? Number((n / 100).toFixed(8)) : 0;
}
// Stored fraction → rendered percent label ("0.70%").
function pct(fraction) {
    if (fraction == null) return '—';
    return `${Number((Number(fraction) * 100).toFixed(4))}%`;
}

function apiError(e) {
    return e?.response?.data?.message || e?.message || 'Something went wrong';
}

/* ── Field validation (mirrors backend patterns) ── */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+]?[0-9 ()\-]{6,20}$/;

const MAX = { name: 255, code: 64, contactName: 255, email: 255, phone: 64, abn: 20, address: 512 };

// Each returns an error string, or '' if valid. Empty optional fields pass.
function vRequired(v, label, max) {
    const t = (v ?? '').trim();
    if (!t) return `${label} is required.`;
    if (t.length > max) return `${label} must be ${max} characters or fewer.`;
    return '';
}
function vOptionalLen(v, label, max) {
    const t = (v ?? '').trim();
    if (t && t.length > max) return `${label} must be ${max} characters or fewer.`;
    return '';
}
function vEmail(v) {
    const t = (v ?? '').trim();
    if (!t) return '';
    if (t.length > MAX.email) return `Email must be ${MAX.email} characters or fewer.`;
    return EMAIL_RE.test(t) ? '' : 'Enter a valid email address.';
}
function vPhone(v) {
    const t = (v ?? '').trim();
    if (!t) return '';
    if (t.length > MAX.phone) return `Phone must be ${MAX.phone} characters or fewer.`;
    return PHONE_RE.test(t) ? '' : 'Enter a valid phone number.';
}
function vAbn(v) {
    const t = (v ?? '').trim();
    if (!t) return '';
    return /^[0-9]{11}$/.test(t) ? '' : 'ABN must be 11 digits.';
}
// Percent input (string). Required, numeric, 0–10% (backend bounds fraction 0.0–0.1).
function vRate(v, label) {
    const t = (v ?? '').trim();
    if (!t) return `${label} is required.`;
    const n = Number(t);
    if (!Number.isFinite(n)) return `${label} must be a number.`;
    if (n < 0 || n > 10) return `${label} must be between 0 and 10%.`;
    return '';
}
// True if every value in an errors object is empty.
function noErrors(errs) {
    return Object.values(errs).every((e) => !e);
}